import axiosInstance from '@/axiosInstance';
import type { ModelRef, Subcategory } from '@/apiServices/types';

export interface Category {
  categoryID: number;
  eliCategory: string;
  name: string;
}

/**
 * R380 "Categories" grid row (PR-877). The five reference-value columns carry both the stored code
 * and the resolved display name; the grid binds the `*Name` variants.
 */
export interface CategoryGridRow {
  categoryID: number;
  eliCategory: string;
  name: string;
  role: string;
  roleName: string;
  tagRequirement: string;
  tagRequirementName: string;
  serialPresence: string;
  serialPresenceName: string;
  siteRequirement?: string | null;
  siteRequirementName?: string | null;
  class?: string | null;
  className?: string | null;
  capacityUnits?: string | null;
  siteQtyMin?: number | null;
  siteQtyMax?: number | null;
  pmNeeded: boolean;
  commissionedIndependently: boolean;
  canBeUncontained: boolean;
  notes?: string | null;
  // Null rather than 0 when the category has no models, matching R380's nullif(...,0).
  totalModels?: number | null;
  specificModels?: number | null;
  genericModels?: number | null;
  children?: string | null;
  parent?: string | null;
  manufacturers?: string | null;
}

export interface CategoryGridPage {
  rows: CategoryGridRow[];
  totalRecords: number;
}

export interface CategoryFilterRequest {
  page?: number;
  rows?: number;
  searchQuery?: string;
  role?: string;
  class?: string;
  pmNeededOnly?: boolean;
  withModels?: string;
  manufacturerId?: number;
}

export interface CategoryLookupItem {
  code: string;
  name: string;
}

export interface CategoryFilterOption {
  id: number;
  name: string;
}

export interface CategoryFilterOptions {
  roles: CategoryLookupItem[];
  classes: CategoryLookupItem[];
  manufacturers: CategoryFilterOption[];
}

export interface CategoryChildren {
  subcategories: Subcategory[];
  models: ModelRef[];
}

export default {
  async getCategories(): Promise<Category[]> {
    try {
      const response = await axiosInstance.get('/api/Category');
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  async getCategoryGrid(request: CategoryFilterRequest): Promise<CategoryGridPage> {
    try {
      const response = await axiosInstance.post('/api/Category/grid', request);
      return {
        rows: response.data?.rows ?? [],
        totalRecords: response.data?.totalRecords ?? 0,
      };
    } catch (error) {
      console.error('Error fetching category grid:', error);
      throw error;
    }
  },

  async getCategoryFilterOptions(): Promise<CategoryFilterOptions> {
    try {
      const response = await axiosInstance.get('/api/Category/filter-options');
      return {
        roles: response.data?.roles ?? [],
        classes: response.data?.classes ?? [],
        manufacturers: response.data?.manufacturers ?? [],
      };
    } catch (error) {
      console.error('Error fetching category filter options:', error);
      throw error;
    }
  },

  async getCategoryChildren(categoryId: number): Promise<CategoryChildren> {
    try {
      const response = await axiosInstance.get(`/api/Category/${categoryId}/children`);
      return {
        subcategories: response.data?.subcategories ?? [],
        models: response.data?.models ?? [],
      };
    } catch (error) {
      console.error('Error fetching category children:', error);
      throw error;
    }
  },
};
